import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
} from '@mui/material';
import { ExpandMoreRounded, LocalHospitalRounded } from '@mui/icons-material';
import { format, isToday, isYesterday } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { dateLocale } from 'i18n';
import DashboardPageHeader from 'components/common/DashboardPageHeader';
import DischargeInstructionCards from 'components/dashboard/discharge/DischargeInstructionCards';
import DischargePdfButton from 'components/dashboard/discharge/DischargePdfButton';
import DischargeMedicationPrompt from 'components/dashboard/discharge/DischargeMedicationPrompt';
import { useAuth } from 'hooks/useAuth';
import { db } from 'services/firebase';

const deliveryDate = (createdAt) => (createdAt?.toDate ? createdAt.toDate() : null);

// The date is what tells one visit from the next, so it is the headline.
const formatVisitDate = (date, t) => {
  if (!date) return t('discharge.title');
  if (isToday(date)) return t('discharge.today');
  if (isYesterday(date)) return t('discharge.yesterday');
  return format(date, t('discharge.dateFormat'), { locale: dateLocale() });
};

const DischargeDelivery = ({ delivery, defaultExpanded }) => {
  const { t } = useTranslation('medications');
  const date = deliveryDate(delivery.createdAt);
  // Time always, so two visits on the same day read apart. The sender only when
  // known: a stand-in like "Your provider" repeated down the list says nothing.
  const detail = [
    date ? format(date, 'p', { locale: dateLocale() }) : null,
    [delivery.providerName, delivery.organizationName].filter(Boolean).join(', '),
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Accordion disableGutters defaultExpanded={defaultExpanded}>
      <AccordionSummary expandIcon={<ExpandMoreRounded />}>
        <Stack spacing={0.25} sx={{ width: '100%', minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            {formatVisitDate(date, t)}
          </Typography>
          {detail && (
            <Typography variant="body2" color="text.secondary" noWrap>
              {detail}
            </Typography>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails>
        <DischargeMedicationPrompt deliveryId={delivery.id} instructions={delivery.instructions} />
        <DischargeInstructionCards instructions={delivery.instructions} />
        <DischargePdfButton instructions={delivery.instructions} placement="bottom" />
      </AccordionDetails>
    </Accordion>
  );
};

const DischargeInstructionsPage = () => {
  const { t } = useTranslation('medications');
  const { user } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!user?.uid) return undefined;
    setLoading(true);
    const unsubscribe = db
      .collection('users')
      .doc(user.uid)
      .collection('dischargeInstructions')
      .orderBy('createdAt', 'desc')
      .onSnapshot(
        (snap) => {
          setDeliveries(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
          setLoading(false);
        },
        (err) => {
          console.error('[DischargeInstructionsPage] subscription failed:', err);
          setError(t('discharge.loadError'));
          setLoading(false);
        },
      );
    return () => unsubscribe();
  }, [user?.uid]);

  return (
    <div className="inner">
      <DashboardPageHeader
        title={t('discharge.title')}
        subtitle={t('discharge.subtitle')}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : deliveries.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <LocalHospitalRounded sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography variant="body1" color="text.secondary">
            {t('discharge.empty.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('discharge.empty.body')}
          </Typography>
        </Box>
      ) : (
        <Stack spacing={1.5}>
          {/* Newest open: it is almost always the one the patient came for. The rest stay
              collapsed so the list still reads as a history. */}
          {deliveries.map((delivery, index) => (
            <DischargeDelivery
              key={delivery.id}
              delivery={delivery}
              defaultExpanded={index === 0}
            />
          ))}
        </Stack>
      )}
    </div>
  );
};

export default DischargeInstructionsPage;
